import {Children} from 'react'
import type {ReactNode} from 'react'
import type {Components} from 'react-markdown'
import {extractText, slugify} from './docsUtils'

export const getMarkdownComponents = (isDark: boolean): Components => ({
  h2: ({children}) => {
    const text = extractText(children)
    return (
      <h2
        id={slugify(text)}
        className={`mt-8 scroll-mt-24 text-[1.625rem] font-semibold tracking-tight first:mt-0 ${isDark ? 'text-[var(--color-text-primary)]' : 'text-slate-900'}`}
      >
        {children}
      </h2>
    )
  },
  h3: ({children}) => {
    const text = extractText(children)
    return (
      <h3
        id={slugify(text)}
        className={`mt-6 scroll-mt-24 text-[1.3rem] font-semibold tracking-tight ${isDark ? 'text-[var(--color-text-primary)]' : 'text-slate-900'}`}
      >
        {children}
      </h3>
    )
  },
  p: ({children}) => (
    <p
      className={`mt-2.5 text-[17px] leading-8 first:mt-0 ${isDark ? 'text-[var(--color-text-secondary)]' : 'text-slate-600'}`}
    >
      {children}
    </p>
  ),
  ul: ({children}) => (
    <ul
      className={`mt-2.5 list-disc pl-5 text-[17px] leading-8 ${isDark ? 'text-[var(--color-text-secondary)]' : 'text-slate-600'}`}
    >
      {children}
    </ul>
  ),
  ol: ({children}) => (
    <ol
      className={`mt-2.5 list-decimal pl-5 text-[17px] leading-8 ${isDark ? 'text-[var(--color-text-secondary)]' : 'text-slate-600'}`}
    >
      {children}
    </ol>
  ),
  li: ({children}) => <li className="mb-1 pl-1 last:mb-0 [&>p]:mt-0 [&>p]:leading-8 [&>p]:text-inherit">{children}</li>,
  blockquote: ({children}) => (
    <blockquote
      className={`mt-4 border-l-4 px-5 py-3 text-[17px] leading-8 ${
        isDark ? 'border-blue-400 bg-blue-950/40 text-blue-200' : 'border-blue-500 bg-blue-50 text-blue-900'
      }`}
    >
      <div className="space-y-0 [&_strong]:font-semibold [&>p]:mt-0 [&>p]:leading-8 [&>p]:text-inherit">{children}</div>
    </blockquote>
  ),
  table: ({children}) => (
    <div
      className={`mt-4 overflow-x-auto rounded-lg border ${isDark ? 'border-[var(--color-border-default)]' : 'border-slate-200'}`}
    >
      <table
        className={`min-w-full divide-y text-[17px] ${isDark ? 'divide-[var(--color-border-default)]' : 'divide-slate-200'}`}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({children}) => (
    <thead className={isDark ? 'bg-[var(--color-background-layer-01)]' : 'bg-slate-100'}>{children}</thead>
  ),
  tbody: ({children}) => (
    <tbody className={`divide-y ${isDark ? 'divide-[var(--color-border-default)]' : 'divide-slate-200'}`}>
      {children}
    </tbody>
  ),
  tr: ({children}) => <tr>{children}</tr>,
  th: ({children}) => (
    <th
      className={`px-4 py-2.5 text-left text-sm font-semibold tracking-wide uppercase ${isDark ? 'text-[var(--color-text-primary)]' : 'text-slate-900'}`}
    >
      {children}
    </th>
  ),
  td: ({children}) => (
    <td
      className={`px-4 py-2.5 text-[17px] leading-7 ${isDark ? 'text-[var(--color-text-secondary)]' : 'text-slate-600'}`}
    >
      {children}
    </td>
  ),
  hr: () => null,
  pre: ({children}) => <>{children}</>,
  code: ({className, children}) => {
    const content = String(children).replace(/\n$/, '')
    const isBlock = Boolean(className)

    return isBlock ? (
      <pre
        className={`mt-6 overflow-x-auto rounded-xl border bg-slate-950 px-4 py-3.5 text-sm leading-7 text-slate-100 ${isDark ? 'border-[var(--color-border-default)]' : 'border-slate-200'}`}
      >
        <code className={className}>{content}</code>
      </pre>
    ) : (
      <code
        className={`rounded px-1.5 py-0.5 text-[0.9em] ${isDark ? 'bg-[var(--color-background-layer-01)] text-[var(--color-text-primary)]' : 'bg-slate-100 text-slate-900'}`}
      >
        {children}
      </code>
    )
  },
  a: ({href, children}) => (
    <a
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      className={`font-medium underline underline-offset-4 ${isDark ? 'text-[var(--color-link-default)]' : 'text-blue-700'}`}
    >
      {children}
    </a>
  ),
})

export const getHighlightOverrides = (
  isDark: boolean,
  highlightQuery: string,
  highlightChildren: (children: ReactNode, query: string) => ReactNode,
): Partial<Components> => ({
  p: ({children}) => (
    <p
      className={`mt-2.5 text-[17px] leading-8 first:mt-0 ${isDark ? 'text-[var(--color-text-secondary)]' : 'text-slate-600'}`}
    >
      {highlightChildren(children, highlightQuery)}
    </p>
  ),
  li: ({children}) => (
    <li className="mb-1 pl-1 last:mb-0 [&>p]:mt-0 [&>p]:leading-8 [&>p]:text-inherit">
      {highlightChildren(children, highlightQuery)}
    </li>
  ),
  td: ({children}) => (
    <td
      className={`px-4 py-2.5 text-[17px] leading-7 ${isDark ? 'text-[var(--color-text-secondary)]' : 'text-slate-600'}`}
    >
      {highlightChildren(children, highlightQuery)}
    </td>
  ),
  th: ({children}) => (
    <th
      className={`px-4 py-2.5 text-left text-sm font-semibold tracking-wide uppercase ${isDark ? 'text-[var(--color-text-primary)]' : 'text-slate-900'}`}
    >
      {highlightChildren(children, highlightQuery)}
    </th>
  ),
  h2: ({children}) => {
    const text = extractText(children)
    const id = slugify(text)
    return (
      <h2
        id={id}
        className={`mt-8 scroll-mt-24 text-[1.625rem] font-semibold tracking-tight first:mt-0 ${isDark ? 'text-[var(--color-text-primary)]' : 'text-slate-900'}`}
      >
        {highlightChildren(children, highlightQuery)}
      </h2>
    )
  },
  h3: ({children}) => {
    const text = extractText(children)
    const id = slugify(text)
    return (
      <h3
        id={id}
        className={`mt-6 scroll-mt-24 text-[1.3rem] font-semibold tracking-tight ${isDark ? 'text-[var(--color-text-primary)]' : 'text-slate-900'}`}
      >
        {highlightChildren(children, highlightQuery)}
      </h3>
    )
  },
})

export const HighlightText = ({text, highlight}: {text: string; highlight: string}) => {
  if (!highlight.trim()) {
    return <>{text}</>
  }

  const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
  const isDarkMode = document.documentElement.classList.contains('dark')

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <mark
            key={index}
            className={`rounded px-0.5 ${isDarkMode ? 'bg-yellow-800 text-yellow-100' : 'bg-yellow-200'}`}
          >
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  )
}

export const highlightChildren = (children: ReactNode, query: string): ReactNode => {
  return Children.map(children, child => {
    if (typeof child === 'string') {
      return <HighlightText text={child} highlight={query} />
    }
    if (typeof child === 'number') {
      return <HighlightText text={String(child)} highlight={query} />
    }
    return child
  })
}

export const ChevronIcon = ({expanded, isDark}: {expanded: boolean; isDark: boolean}) => {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-4 w-4 items-center justify-center transition-transform duration-200 ease-out ${
        isDark ? 'text-[var(--color-icon-default)]' : 'text-slate-400'
      } ${expanded ? 'rotate-90' : 'rotate-0'}`}
    >
      <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 fill-none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6 3.5L10.5 8L6 12.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}
